export default function WellingtonDestinationsSection() {
  const stats = [
    {
      value: "58%",
      label: "of Wellington requests are equestrian-related or competition travel with under 3 weeks notice",
    },
    {
      value: "2–3 weeks",
      label: "average notice for European equestrian competition and horse auction travel",
    },
    {
      value: "Nov–Apr",
      label: "peak equestrian season — passport renewals spike before WEF and polo season departures",
    },
    {
      value: "36-hour",
      label: "shortest competition departure window we've accommodated for a Wellington trainer",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "Belgium & The Netherlands",
      context: "The most common European destination for Wellington equestrian professionals — horse auctions in Belgium (Zangersheide, EuroHorse) and the Netherlands (KWPN, VDL Stud) draw trainers, buyers, and veterinarians from Wellington's competitive circuit. These trips are often planned with 1–2 weeks of notice when a specific horse becomes available.",
      urgency: "Auction bidding windows are typically 48–72 hours — passport issues discovered during pre-auction travel planning are catastrophic",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London, Windsor, and Hickstead host major show jumping and dressage competitions that draw Wellington riders every summer. The Royal Windsor Horse Show, London International Horse Show, and Burghley Horse Trials are fixtures on the Wellington competition calendar. Polo teams also travel to England for the Cartier Queen's Cup and Gold Cup.",
      urgency: "Competition entry deadlines are typically 2–4 weeks before the event — insufficient time for standard passport processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Wellington families — whether by cruise from Port Everglades or Fort Lauderdale, or by direct flight from PBI to Nassau. Families from Wellington's gated communities regularly sail to the Bahamas, Jamaica, the Cayman Islands, and the Eastern Caribbean. All cruise passengers and most air travelers need valid passports.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Argentina",
      context: "Argentina is the spiritual home of polo, and Wellington polo professionals travel to Buenos Aires regularly for the Argentine Open, the world's most prestigious polo tournament. Argentine players also rotate into Wellington's International Polo Club season. These trips are seasonal, competitive, and often planned on short notice.",
      urgency: "Polo season rotation and tournament invitations often arrive with 2–3 weeks of notice — too short for standard processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "France, Germany & Monaco",
      context: "Paris hosts the Longines Global Champions Tour, Monaco hosts the Monte-Carlo leg, and Germany's CHIO Aachen is the premier show jumping competition in Europe. Wellington's elite riders and their support teams travel to these events annually. France and Germany also host major horse auctions and veterinary conferences.",
      urgency: "European competition calendars are published months in advance, but actual travel bookings may happen only 2–4 weeks before departure",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Latin America",
      context: "Cancun, Riviera Maya, and Los Cabos are staple destinations for Wellington families seeking beach vacations within a short flight. PBI offers connecting service through Atlanta, Charlotte, and Houston. Business travelers also fly to Mexico City for corporate meetings. Mexico requires six months of passport validity for entry.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March for Wellington families",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Wellington Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Wellington
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Wellington travelers head to Europe for equestrian competition, the Caribbean for family cruises and resorts, and South America for polo. These are the destinations that create the most passport urgency for our Wellington clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout South Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Palm Beach Gardens", href: "/palm-beach-gardens" },
              { name: "Jupiter", href: "/jupiter" },
              { name: "West Palm Beach", href: "/west-palm-beach" },
              { name: "Boca Raton", href: "/boca-raton" },
              { name: "Delray Beach", href: "/delray-beach" },
              { name: "Naples", href: "/naples" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}